import { useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Brain, PenLine, BookOpen, ChevronRight, Loader2, Trophy, AlertTriangle, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getMicStream, micErrorMessage } from "@/lib/mic";
import { useAuth } from "@/lib/auth";

const TECHNIQUES = [
  {
    key: "feynman",
    label: "Feynman Technique",
    icon: Brain,
    color: "#B8A4FF",
    bg: "rgba(184,164,255,0.08)",
    border: "rgba(184,164,255,0.25)",
    desc: "Explain a concept in your own words as if teaching a child. Gaps become obvious instantly.",
    plusRequired: true,
  },
  {
    key: "blurting",
    label: "Blurting",
    icon: PenLine,
    color: "#5AC8FA",
    bg: "rgba(90,200,250,0.08)",
    border: "rgba(90,200,250,0.25)",
    desc: "Write down everything you remember from the study without looking at your notes.",
    plusRequired: true,
  },
  {
    key: "sq3r",
    label: "SQ3R Method",
    icon: BookOpen,
    color: "#34D399",
    bg: "rgba(52,211,153,0.08)",
    border: "rgba(52,211,153,0.25)",
    desc: "Survey → Question → Read → Recite → Review. A structured deep-reading method.",
    plusRequired: true,
  },
];

function GradeChip({ grade, pct }) {
  const colors = { A: "#34D399", B: "#5AC8FA", C: "#F59E0B", D: "#EF4444" };
  const c = colors[grade] || "#B0AEBC";
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-bold"
      style={{ background: `${c}18`, border: `1px solid ${c}40`, color: c }}>
      Grade {grade}{pct !== undefined ? ` — ${pct}%` : ""}
    </span>
  );
}

// ── Feynman Panel ─────────────────────────────────────────────────────────────
function FeynmanPanel({ scId, onBack }) {
  const [concept, setConcept] = useState("");
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await getMicStream();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "explanation.webm");
          const r = await api.post("/techniques/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const txt = (r.data.text || "").trim();
          if (txt) {
            setExplanation((prev) => (prev.trim() ? prev.trim() + " " + txt : txt));
            toast.success("Transcribed your explanation");
          } else {
            toast.error("Couldn't hear anything — try again");
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed");
        } finally { setTranscribing(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(`${micErrorMessage(e)} You can type your explanation instead.`, { duration: 7000 });
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const submit = async () => {
    if (!concept.trim() || !explanation.trim()) return;
    setLoading(true);
    try {
      const r = await api.post("/techniques/feynman", { sc_id: scId, concept, explanation });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluation failed");
    } finally { setLoading(false); }
  };

  if (result) return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <GradeChip grade={result.grade} />
        <span className="text-[13px] text-[#B0AEBC]">Score {result.total}/9</span>
      </div>
      <p className="text-[15px] text-[#B8A4FF] italic">"{result.praise}"</p>

      <div className="grid grid-cols-3 gap-3">
        {[["Clarity", result.clarity], ["Accuracy", result.accuracy], ["Completeness", result.completeness]].map(([lbl, val]) => (
          <div key={lbl} className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div className="text-[22px] font-bold text-white">{val}/3</div>
            <div className="text-[11px] text-[#B0AEBC] mt-0.5">{lbl}</div>
          </div>
        ))}
      </div>

      {result.gaps?.length > 0 && (
        <div className="rounded-xl p-4" style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)" }}>
          <p className="text-[12px] font-semibold text-red-400 mb-2">GAPS TO FILL</p>
          <ul className="space-y-1">{result.gaps.map((g, i) => <li key={i} className="text-[13px] text-[#F4F4F5] flex gap-2"><span className="text-red-400 mt-0.5">•</span>{g}</li>)}</ul>
        </div>
      )}
      {result.tips?.length > 0 && (
        <div className="rounded-xl p-4" style={{ background: "rgba(184,164,255,0.07)", border: "1px solid rgba(184,164,255,0.2)" }}>
          <p className="text-[12px] font-semibold text-[#B8A4FF] mb-2">IMPROVEMENT TIPS</p>
          <ul className="space-y-1">{result.tips.map((t, i) => <li key={i} className="text-[13px] text-[#F4F4F5] flex gap-2"><span className="text-[#B8A4FF] mt-0.5">→</span>{t}</li>)}</ul>
        </div>
      )}
      <button onClick={() => { setResult(null); setExplanation(""); }} className="btn btn-secondary w-full py-3">Try again</button>
    </motion.div>
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Concept to explain</label>
        <input value={concept} onChange={e => setConcept(e.target.value)} className="input"
          placeholder="e.g. Newton's 2nd law, Photosynthesis…" data-testid="feynman-concept" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[12px] font-medium text-[#B0AEBC]">
            Your explanation <span className="text-[#555]">(pretend you're explaining to a 10-year-old)</span>
          </label>
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={transcribing}
            data-testid="feynman-record-btn"
            className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-full transition-colors disabled:opacity-50 ${
              recording
                ? "bg-red-500/15 text-red-400 border border-red-400/30"
                : "bg-[#7C5CFF]/12 text-[#B8A4FF] border border-[#7C5CFF]/25 hover:bg-[#7C5CFF]/20"
            }`}
          >
            {transcribing ? (
              <><Loader2 size={12} className="animate-spin" /> Transcribing…</>
            ) : recording ? (
              <><Square size={11} className="fill-current" /> Stop</>
            ) : (
              <><Mic size={12} /> Explain out loud</>
            )}
          </button>
        </div>
        <textarea value={explanation} onChange={e => setExplanation(e.target.value)}
          className="input min-h-[180px] resize-none"
          placeholder="Write your explanation here, or tap 'Explain out loud' to speak it. Use simple language, no jargon…"
          data-testid="feynman-explanation" />
        {recording && (
          <p className="text-[11.5px] text-red-400 mt-1.5 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Recording… tap Stop when you're done.
          </p>
        )}
      </div>
      <button onClick={submit} disabled={loading || !concept.trim() || !explanation.trim()}
        className="btn btn-primary w-full py-3.5" data-testid="feynman-submit">
        {loading ? <><Loader2 size={15} className="animate-spin inline mr-2" />Evaluating…</> : "Evaluate my explanation"}
      </button>
    </div>
  );
}

// ── Blurting Panel ────────────────────────────────────────────────────────────
function BlurtingPanel({ scId }) {
  const [dump, setDump] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!dump.trim()) return;
    setLoading(true);
    try {
      const r = await api.post("/techniques/blurting", { sc_id: scId, brain_dump: dump });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluation failed");
    } finally { setLoading(false); }
  };

  if (result) return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
      <div className="flex items-center gap-3">
        <GradeChip grade={result.grade} pct={result.coverage_pct} />
      </div>
      <p className="text-[15px] text-[#5AC8FA] italic">"{result.praise}"</p>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl p-4" style={{ background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.2)" }}>
          <p className="text-[11px] font-semibold text-green-400 mb-2">RECALLED ({result.recalled?.length || 0})</p>
          <ul className="space-y-1">{(result.recalled || []).map((c, i) => <li key={i} className="text-[12px] text-[#F4F4F5]">✓ {c}</li>)}</ul>
        </div>
        <div className="rounded-xl p-4" style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)" }}>
          <p className="text-[11px] font-semibold text-red-400 mb-2">MISSED ({result.missed?.length || 0})</p>
          <ul className="space-y-1">{(result.missed || []).map((c, i) => <li key={i} className="text-[12px] text-[#F4F4F5]">✗ {c}</li>)}</ul>
        </div>
      </div>
      {result.tips?.length > 0 && (
        <div className="rounded-xl p-4" style={{ background: "rgba(90,200,250,0.07)", border: "1px solid rgba(90,200,250,0.2)" }}>
          <p className="text-[12px] font-semibold text-[#5AC8FA] mb-2">REVISION TIPS</p>
          <ul className="space-y-1">{result.tips.map((t, i) => <li key={i} className="text-[13px] text-[#F4F4F5] flex gap-2"><span className="text-[#5AC8FA] mt-0.5">→</span>{t}</li>)}</ul>
        </div>
      )}
      <button onClick={() => { setResult(null); setDump(""); }} className="btn btn-secondary w-full py-3">Try again</button>
    </motion.div>
  );

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-[#B0AEBC]">Close your notes. Write down everything you remember from this study in the box below. Don't look up anything — that's the point.</p>
      <textarea value={dump} onChange={e => setDump(e.target.value)}
        className="input min-h-[240px] resize-none"
        placeholder="Start writing everything you remember…"
        data-testid="blurting-textarea" />
      <div className="flex justify-between items-center text-[12px] text-[#555]">
        <span>{dump.split(/\s+/).filter(Boolean).length} words</span>
        <span>Aim for at least 100 words</span>
      </div>
      <button onClick={submit} disabled={loading || dump.split(/\s+/).filter(Boolean).length < 10}
        className="btn btn-primary w-full py-3.5" data-testid="blurting-submit">
        {loading ? <><Loader2 size={15} className="animate-spin inline mr-2" />Checking coverage…</> : "Check my recall"}
      </button>
    </div>
  );
}

// ── SQ3R Panel ────────────────────────────────────────────────────────────────
function SQ3RPanel({ scId }) {
  const [phase, setPhase] = useState("start"); // start | survey | read | recite | result
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null); // {session_id, headings, focus_questions}
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/techniques/sq3r/start", { sc_id: scId });
      setSession(r.data);
      setAnswers(new Array(r.data.focus_questions.length).fill(""));
      setPhase("survey");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
    finally { setLoading(false); }
  };

  const submitRecite = async () => {
    setLoading(true);
    try {
      const r = await api.post("/techniques/sq3r/recite", {
        sc_id: scId, session_id: session.session_id, answers
      });
      setResult(r.data);
      setPhase("result");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setLoading(false); }
  };

  const verdictColor = (v) => ({ correct: "#34D399", partial: "#F59E0B", incorrect: "#EF4444" }[v] || "#B0AEBC");

  if (phase === "start") return (
    <div className="space-y-5 text-center">
      <div className="rounded-xl p-5 text-left space-y-2" style={{ background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.2)" }}>
        {["Survey — scan headings & structure", "Question — form focus questions", "Read — study actively guided by questions", "Recite — answer from memory", "Review — see gaps & get tips"].map((s, i) => (
          <div key={i} className="flex items-center gap-3 text-[13px]">
            <span className="w-5 h-5 rounded-full bg-green-400/20 text-green-400 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
            <span className="text-[#F4F4F5]">{s}</span>
          </div>
        ))}
      </div>
      <button onClick={start} disabled={loading} className="btn btn-primary w-full py-3.5" data-testid="sq3r-start">
        {loading ? <><Loader2 size={15} className="animate-spin inline mr-2" />Preparing session…</> : "Start SQ3R Session"}
      </button>
    </div>
  );

  if (phase === "survey") return (
    <div className="space-y-5">
      <div>
        <p className="text-[11px] font-semibold text-[#34D399] mb-2">SURVEY — KEY SECTIONS</p>
        <div className="rounded-xl p-4 space-y-1" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          {session?.headings?.map((h, i) => <p key={i} className="text-[13px] text-[#F4F4F5]">• {h}</p>)}
        </div>
      </div>
      <div>
        <p className="text-[11px] font-semibold text-[#5AC8FA] mb-2">QUESTION — FOCUS QUESTIONS WHILE YOU READ</p>
        <div className="space-y-2">
          {session?.focus_questions?.map((q, i) => (
            <div key={i} className="rounded-lg px-4 py-2.5 text-[13px] text-[#F4F4F5]"
              style={{ background: "rgba(90,200,250,0.07)", border: "1px solid rgba(90,200,250,0.15)" }}>
              {i + 1}. {q}
            </div>
          ))}
        </div>
      </div>
      <button onClick={() => setPhase("read")} className="btn btn-primary w-full py-3.5">I've read the material — Recite</button>
    </div>
  );

  if (phase === "read") return (
    <div className="space-y-5">
      <p className="text-[13px] text-[#B0AEBC]">Now go read your study notes with these questions in mind. When you're ready, come back and answer from memory (don't look!).</p>
      <div className="space-y-2">
        {session?.focus_questions?.map((q, i) => (
          <div key={i} className="space-y-1.5">
            <p className="text-[12px] font-medium text-[#B0AEBC]">Q{i + 1}: {q}</p>
            <textarea value={answers[i] || ""} onChange={e => setAnswers(a => { const n = [...a]; n[i] = e.target.value; return n; })}
              className="input min-h-[70px] resize-none text-[13px]"
              placeholder="Answer from memory…" data-testid={`sq3r-answer-${i}`} />
          </div>
        ))}
      </div>
      <button onClick={submitRecite} disabled={loading || answers.every(a => !a.trim())}
        className="btn btn-primary w-full py-3.5" data-testid="sq3r-submit">
        {loading ? <><Loader2 size={15} className="animate-spin inline mr-2" />Reviewing…</> : "Submit answers"}
      </button>
    </div>
  );

  if (phase === "result" && result) return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
      <div className="flex items-center gap-3">
        <span className="text-[15px] font-semibold text-white">{result.overall_pct}% overall</span>
        <Trophy size={16} className="text-[#F59E0B]" />
      </div>
      <p className="text-[14px] text-[#B0AEBC] italic">{result.summary}</p>
      <div className="space-y-3">
        {(result.scores || []).map((s, i) => (
          <div key={i} className="rounded-xl p-3 space-y-1" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${verdictColor(s.verdict)}30` }}>
            <div className="flex items-center justify-between">
              <p className="text-[12px] font-medium text-[#F4F4F5]">{s.q}</p>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${verdictColor(s.verdict)}18`, color: verdictColor(s.verdict) }}>{s.verdict}</span>
            </div>
            <p className="text-[12px] text-[#B0AEBC]">{s.feedback}</p>
          </div>
        ))}
      </div>
      {result.review_tips?.length > 0 && (
        <div className="rounded-xl p-4" style={{ background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.2)" }}>
          <p className="text-[12px] font-semibold text-green-400 mb-2">REVIEW TIPS</p>
          <ul className="space-y-1">{result.review_tips.map((t, i) => <li key={i} className="text-[13px] text-[#F4F4F5] flex gap-2"><span className="text-green-400">→</span>{t}</li>)}</ul>
        </div>
      )}
      <button onClick={() => { setPhase("start"); setSession(null); setAnswers([]); setResult(null); }}
        className="btn btn-secondary w-full py-3">Start new session</button>
    </motion.div>
  );

  return null;
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function StudyTechniques() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [active, setActive] = useState(null);

  const isPlusOrPro = user?.plan === "plus" || user?.plan === "pro";

  if (!isPlusOrPro) return (
    <div className="max-w-[760px] mx-auto px-5 py-12">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-[#B0AEBC] hover:text-white text-[13px] mb-8">
        <ArrowLeft size={15} /> Back
      </button>
      <div className="rounded-2xl p-8 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        <AlertTriangle size={32} className="mx-auto mb-4 text-[#F59E0B]" />
        <h2 className="text-[22px] font-semibold mb-2">Plus feature</h2>
        <p className="text-[#B0AEBC] text-[15px] mb-6">Study Techniques (Feynman, Blurting, SQ3R) are available on the Plus and Pro plans.</p>
        <Link to="/app/billing" className="btn btn-primary px-8 py-3">Upgrade to Plus</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-[760px] mx-auto px-5 py-10">
      <button onClick={() => active ? setActive(null) : nav(-1)}
        className="flex items-center gap-2 text-[#B0AEBC] hover:text-white text-[13px] mb-8 transition-colors">
        <ArrowLeft size={15} /> {active ? "Back to techniques" : "Back to study"}
      </button>

      <AnimatePresence mode="wait">
        {!active ? (
          <motion.div key="list" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <h1 className="text-[28px] font-bold mb-2">Study Techniques</h1>
            <p className="text-[15px] text-[#B0AEBC] mb-8">Active recall methods proven to boost retention and exam performance.</p>
            <div className="space-y-3">
              {TECHNIQUES.map(t => (
                <button key={t.key} onClick={() => setActive(t.key)}
                  data-testid={`technique-${t.key}`}
                  className="w-full rounded-2xl p-5 text-left flex items-center justify-between group transition-all"
                  style={{ background: t.bg, border: `1px solid ${t.border}` }}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${t.color}18`, border: `1px solid ${t.color}30` }}>
                      <t.icon size={18} style={{ color: t.color }} />
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-white">{t.label}</p>
                      <p className="text-[13px] text-[#B0AEBC] mt-0.5">{t.desc}</p>
                    </div>
                  </div>
                  <ChevronRight size={17} className="text-[#555] group-hover:text-white transition-colors ml-4 flex-shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div key={active} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            {(() => {
              const t = TECHNIQUES.find(x => x.key === active);
              return (
                <>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: `${t.color}18`, border: `1px solid ${t.color}30` }}>
                      <t.icon size={16} style={{ color: t.color }} />
                    </div>
                    <div>
                      <h2 className="text-[20px] font-bold">{t.label}</h2>
                      <p className="text-[13px] text-[#B0AEBC]">{t.desc}</p>
                    </div>
                  </div>
                  {active === "feynman" && <FeynmanPanel scId={id} onBack={() => setActive(null)} />}
                  {active === "blurting" && <BlurtingPanel scId={id} />}
                  {active === "sq3r" && <SQ3RPanel scId={id} />}
                </>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
